import _ from "lodash";
import {
  computed,
  makeObservable,
  observable,
  override,
  action,
  reaction,
} from "mobx";
import config from "../../config";
import {
  getMultiMeasurements,
  postOpsLog,
  getAhiDescriptions,
  getAllPatientAhiData,
} from "../../serviceCalls";
import { fetchMonitorConfig, filterWaveData } from "../Monitor/MonitorStore";

import PatientStore from "../Patients/PatientStore";
import { MODULE_ID } from "../../shared/moduleIds";
import { MRN_STATE } from "../../shared/Patient/MrnState";
import ColorMap from "./colorMap";
import { patientSort } from "../Patients/PatientSort";

export default class SurveilStore extends PatientStore {
  _moduleId = MODULE_ID.PATIENT_MONITORING;
  bed;
  customSortedPatients;
  fontSize = 18;
  layout;
  previewSplitScreen;
  selectedAsmpid;
  showPatientList;
  showPHI = true;
  _splitScreen;
  canvasWorkerIds = "";
  abortControllers = [];
  pmConfigs = {};
  ahiDescriptions = [];
  ahiData = {};
  isSurveil = true;
  loadingConfiguration = true;

  get simpleSearchEnabled() {
    return false;
  }

  get splitScreen() {
    return this._splitScreen;
  }

  set splitScreen(splitScreen) {
    if (splitScreen === this._splitScreen) return;
    this._splitScreen = splitScreen;
    this.postOpsLog({
      operationType: "view selected",
      extraContext: {
        multiLead: splitScreen || undefined,
        singleLeadCondensed: !splitScreen || undefined,
      },
    });
  }

  get groupSortDisabled() {
    if (this.isCustomBedList) return true;
    return false;
  }

  get groupSortActive() {
    return (
      !_.isEqual(_.pickBy(this.groupSort, Boolean), this.defaultGroupSort()) &&
      !this.customSortedPatients
    );
  }

  // TODO: Move to surveilStore superclass.
  sortPatients(patients) {
    let sorted = super.sortPatients(patients);
    const groupBy = this.groupByValue?.value;
    if (this.isCustomBedList) return sorted;
    if (groupBy) {
      sorted = patientSort(
        sorted,
        groupBy,
        this.selectedListOption,
        this.groupByInverted,
      );
    }
    return sorted;
  }

  postOpsLog = ({ operationType, extraContext }) =>
    postOpsLog({
      operationType,
      extraContext: { component: "PM MPV", ...extraContext },
    });

  constructor(props) {
    sessionStorage.moduleId = 3;
    super({ ...props });

    makeObservable(this, {
      bed: observable,
      customSortedPatients: observable,
      fontSize: observable,
      layout: observable,
      previewSplitScreen: observable,
      selectedAsmpid: observable,
      setPatient: action,
      showPatientList: observable,
      showPHI: observable,
      _splitScreen: observable,
      canvasWorkerIds: observable,
      activePatients: computed,
      allowSingle: computed,
      patient: override,
      limitedPatients: computed,
      ahiDescriptions: observable,
      ahiData: observable,
      isTooManyBeds: computed,
      loadingConfiguration: observable,
    });

    reaction(
      () => this.limitedPatients,
      () => this.fetchConfigs(),
    );

    this.colorMap = new ColorMap({ appStore: this.appStore });
    this.$ready = this.$ready.then(async () => {
      await this.patientConfigPromise;
      const res = await this.fetchPatientsPromise;
      if (res?.patients?.length) this.colorMap.prune(res);
      while (!this.configsPromise) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      await this.configsPromise;
      await this.renderWorkerReady;
      await new Promise((resolve) => setTimeout(resolve, 100));
      this.loadingConfiguration = false;
    });
  }

  get listIndexKey() {
    return "mpvListIndex";
  }
  get activePatients() {
    return this.patients.filter((p) => p.status === "active");
  }

  get allowSingle() {
    return config.pm.allowMultiPatientSingleLead;
  }

  setPatient(patient) {
    if (this.isCustomBedList && patient) {
      const { unit, location } = this.moduleSpecifics(patient);
      this.bed = { unit, location };
    } else {
      this.bed = undefined;
    }
    this.patient = patient;
  }

  get patient() {
    if (this.selectedAsmpid) {
      return this.patients.find((p) => p.asmpid === this.selectedAsmpid);
    }
    return undefined;
  }

  set patient(patient) {
    this.selectedAsmpid = patient?.asmpid;
  }

  // We can probably turn this up a little.
  maxPatients = 40;

  filterPatients = (_patients) => {
    let patients = super.filterPatients(_patients);
    if (this.searchQuery && this.searchQuery.length > 1)
      patients = patients.filter(this.searchFilter);

    return patients;
  };

  get patients() {
    if (this.customSortedPatients) {
      return this.filterPatients(this.customSortedPatients);
    }
    let patients = this._patients;
    patients = this.filterPatients(patients);
    patients = this.sortPatients(patients);

    return patients;
  }

  set patients(patients) {
    this._patients = patients;
    if (config.ahi.enabled) this.fetchAhiData();
  }

  get limitedPatients() {
    let patients = _.uniqBy(this.patients, "asmpid").slice(0, this.maxPatients);
    return patients;
  }

  get splitScreenPatients() {
    if (this.splitScreen) {
      return this.limitedPatients
        .filter((patient) => patient.status || this.isCustomBedList)
        .slice(0, this.splitScreen);
    }
    return undefined;
  }

  searchFilter = (p) => {
    // search filter
    if (!this.searchQuery || this.searchQuery.length < 2) return true;
    const moduleSpecific = p.moduleSpecifics;
    return (
      (
        `${p.firstName} ${p.lastName} • ${p.age}${p.gender[0]} ` +
        `${moduleSpecific.unit} • ${moduleSpecific.location}`
      )
        .toLowerCase()
        .indexOf(this.searchQuery.toLowerCase()) !== -1
    );
  };

  getBookmark = (asmpid) => {
    return this.measurements?.[asmpid]?.bookmark;
  };

  get focusMode() {
    return (this.patient || this.bed) && !this.splitScreen && this.allowSingle;
  }

  get liveOnly() {
    return !this.splitScreen || this.splitScreen > 4;
  }

  fetchMeasurements = async ({ paused = [] } = {}) => {
    if (this.loadingMeasurements) return;
    const patientsScope = this.splitScreen
      ? this.splitScreenPatients
      : this.limitedPatients;

    const keys = this.splitScreen ? undefined : this.waveKeys;

    const FIXED_BED_ASMPIDS = patientsScope
      .filter((p) => p.mrnState === MRN_STATE.FIXED)
      .map((p) => p.asmpid);

    if (this.measurements) {
      paused.forEach((asmpid) => {
        this.measurements[asmpid] = undefined;
      });
    }

    const ASMPIDS = new Set(
      patientsScope
        .filter((p) => !p.isEmptyBed)
        .map((p) => p.asmpid)
        .concat(FIXED_BED_ASMPIDS)
        .filter((asmpid) =>
          this.splitScreen
            ? !paused.includes(asmpid)
            : this.visiblePatients[asmpid],
        ),
    );
    const leadBufferSeconds = this.splitScreen
      ? 3
      : config.pm.surveilLeadBufferSeconds;
    // TODO: Replace this `15` with a calculated value, which should be calculated in Monitor, and sent back.
    const chartWidthSeconds = this.splitScreen
      ? 15
      : config.pm.mpv.singleLeadSeconds;

    this.abortMeasurementsCalls();

    if (ASMPIDS.size) {
      // split into two, one for bookmarks
      const bookmarkedAsmpids = Array.from(ASMPIDS).filter(this.getBookmark);
      const freshAsmpids = _.difference(Array.from(ASMPIDS), bookmarkedAsmpids);
      const promises = [];
      const isOffline = !navigator.onLine;

      if (bookmarkedAsmpids.length) {
        const controller = new AbortController();
        this.abortControllers.push(controller);
        promises.push(
          getMultiMeasurements({
            asmpids: bookmarkedAsmpids,
            bookmark: bookmarkedAsmpids.map(this.getBookmark),
            keys,
            controller,
            quiet: isOffline ? false : true,
          })
            .catch((err) => {
              console.error("measurements", err);

              if (isOffline) {
                const mockedData = {
                  waveData: [],
                  discreteData: [],
                  eventData: [],
                };

                const finalData = bookmarkedAsmpids.map((asmpid) => ({
                  ...mockedData,
                  asmpid,
                }));

                return finalData;
              }
              return err?.data?.model;
            })
            .finally(() => {
              _.remove(this.abortControllers, (c) => c === controller);
            }),
        );
      }
      if (freshAsmpids.length) {
        const controller = new AbortController();
        this.abortControllers.push(controller);
        promises.push(
          getMultiMeasurements({
            asmpids: freshAsmpids,
            seconds: Math.ceil(chartWidthSeconds + leadBufferSeconds * 2),
            keys,
            controller,
            quiet: isOffline ? false : true,
          })
            .then((res) => {
              // Trigger a reset of cache in the MeasurementsStore.
              Object.values(res).forEach((m) => (m.reset = true));
              return res;
            })
            .catch((err) => {
              console.error("measurements", err);
              if (isOffline) {
                const mockedData = {
                  waveData: [],
                  discreteData: [],
                  eventData: [],
                };

                const finalData = bookmarkedAsmpids.map((asmpid) => ({
                  ...mockedData,
                  asmpid,
                }));

                return finalData;
              }
              return err?.data?.model;
            })
            .finally(() => {
              _.remove(this.abortControllers, (c) => c === controller);
            }),
        );
      }

      if (!promises.length) return;
      this.loadingMeasurements = true;
      return Promise.all(promises)
        .then((models) => {
          if (models)
            models.filter(Boolean).forEach((model) => {
              Object.entries(model).forEach(([asmpid, measurements]) => {
                if (!this.splitScreen && !this.visiblePatients[asmpid]) return;
                //For every patient, update the status based off of what we get back from measurements
                this.patients.forEach((patient, index) => {
                  if (patient.asmpid === asmpid) {
                    if (patient.status === "active" && !measurements.isActive) {
                      this.patients[index].status = "inactive";
                    } else if (
                      patient.status === "inactive" &&
                      measurements.isActive
                    ) {
                      this.patients[index].status = "active";
                    }
                    //Update focus-mode patient
                    if (this.patient === this.patients[index]) {
                      this.patient = this.patients[index];
                    }
                  }
                });
                filterWaveData(measurements);
              });
            });
          // override null bookmarks with stored bookmarks.
          if (models[0] && bookmarkedAsmpids.length) {
            Object.entries(models[0]).forEach(([key, value]) => {
              if (!value.bookmark) {
                value.bookmark = this.getBookmark(key);
              }
            });
          }

          // Combine responses for fresh and bookmarked patients.
          if (models) {
            let arr = [...models.filter(Boolean)];
            if (arr.length) {
              this.measurements = Object.assign(...arr);
            }
          }

          return this.measurements;
        })
        .finally(() => (this.loadingMeasurements = false));
    }
  };

  async fetchConfigs() {
    const asmpids = this.limitedPatients
      .filter((p) => !p.isEmptyBed && !this.pmConfigs[p.asmpid])
      .map((p) => p.asmpid);
    this.configsPromise = Promise.all(
      asmpids.map(async (asmpid) => {
        try {
          this.pmConfigs[asmpid] = await fetchMonitorConfig(asmpid);
          return this.pmConfigs[asmpid];
        } catch (e) {
          console.log("config failed to load");
        }
      }),
    );
    const configs = await this.configsPromise;
    if (configs.filter(Boolean).length) {
      this.pmConfigs = { ...this.pmConfigs };
    }
  }

  get waveKeys() {
    return [
      ...Object.values(this.pmConfigs).reduce((keys, pmConfig) => {
        const key = pmConfig?.waveGroups?.[0]?.key;
        if (key) {
          keys.add(key);
        }
        return keys;
      }, new Set()),
    ];
  }

  // TODO: make a surveilStore superclass, and extend PmSurveilStore from it.
  async getPatients() {
    await super.getPatients(...arguments);
    // New array of patients from getPatients
    let freshPatients = this.patients;

    if (this.customSortedPatients?.length) {
      const patients = this.customSortedPatients
        .map((patient) =>
          freshPatients.find(
            (newPatient) => newPatient.asmpid === patient.asmpid,
          ),
        )
        .filter(Boolean);

      const newPatients = freshPatients.filter(
        (newPatient) =>
          !this.customSortedPatients.find(
            (oldPatient) => oldPatient.asmpid === newPatient.asmpid,
          ),
      );

      // Add the new patients to the end of the list
      const finalPatients = patients.concat(newPatients);
      this.patients = finalPatients;
    }

    if (
      this.isCustomBedList &&
      this.patient?.mrnState === MRN_STATE.FIXED &&
      this.bed
    ) {
      const patient = this.patients.find((p) =>
        _.isMatch(this.moduleSpecifics(p), this.bed),
      );
      this.setPatient(patient);
    }
  }

  visiblePatients = {};
  setPatientVisible = ({ asmpid, visible }) => {
    this.visiblePatients[asmpid] = visible;
    if (!visible && this.measurements?.[asmpid])
      this.measurements.asmpid = undefined;
  };

  get sortableLists() {
    return this.lists.filter(
      (list) =>
        list.moduleId.toString() === MODULE_ID.PATIENT_MONITORING.toString() &&
        (list.type === "census" || list.type === "multi_patient"),
    );
  }

  resetGroupSort() {
    super.resetGroupSort();
    this.resetCustomSort();
  }

  get isTooManyBeds() {
    return !this.splitScreen && this.patients?.length > this.maxPatients;
  }

  inverter(...args) {
    return (...inverted) => {
      super.inverter(...args)(...inverted);
      this.resetCustomSort();
    };
  }

  updateSelect(...args) {
    return (...selectArgs) => {
      super.updateSelect(...args)(...selectArgs);
      this.resetCustomSort();
    };
  }

  resetCustomSort() {
    this.customSortedPatients = undefined;
  }

  abortMeasurementsCalls() {
    while (this.abortControllers.length) {
      const controller = this.abortControllers.pop();
      if (!controller.signal.aborted) controller.abort();
    }
  }

  resetMeasurements() {
    this.abortMeasurementsCalls();
    this.measurements = {};
  }

  customSorts = [];
  listChange(list) {
    this.customSorts[this.listIndex] = this.customSortedPatients;
    this.customSortedPatients = this.customSorts[this.lists.indexOf(list)];
    this.resetCustomSort();
    super.listChange(...arguments);

    this.postOpsLog({
      operationType: "patient list viewed",
      extraContext: { title: list.value.title, type: list.value.type },
    });
  }

  fetchAhiDescriptions() {
    return getAhiDescriptions().then((data) => (this.ahiDescriptions = data));
  }

  fetchAhiData(isRefresh = false) {
    const asmpids = this.limitedPatients
      .filter((p) => !p.isEmptyBed)
      .map((p) => p.asmpid);
    if (asmpids.length && (isRefresh || !Object.keys(this.ahiData).length)) {
      this.ahiDataPromise = getAllPatientAhiData(asmpids)
        .then((data) => {
          this.ahiData = data.result.reduce((total, item) => {
            total[item.value.asmpId] = item.value;
            return total;
          }, {});
        })
        .catch((e) => {
          console.error("fetchAhiData failed", e);
          // TODO: retry?, stop trying?, cool-down?
        });
    }
  }
}
